import React, { useEffect, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import {
  Box,
  Paper,
} from "@mui/material";
import "./DeviceDiagnostics.css";
import { Fragment } from "react";
import MuiDatePicker from "../../Common/FormInputs/MuiDatePicker";
import AppButton from "../../Common/Button/Button";
import EmptyState from "../../Common/EmptyState/EmptyState";
import IncrementalPagination from "../../Common/DataTable/IncrementalPagination";
import DisplayDeviceDiagnosticsData from "./DisplayDeviceDiagnosticsData";
import {
  deviceDiagnosticsPageSize,
  deviceDiagnosticsPageSizeOptions,
  startDateLimit,
} from "./DeviceDiagnosticsConstants";
import { format } from "date-fns";

const DeviceDiagnostics = ({
  setDialog,
  setDialogContents,
  deviceDetails,
  getDeviceDiagnosticsData,
  diagnosticData,
  downloadDeviceDiagnosticReport,
}) => {
  const [deviceDiagnosticsData, setDeviceDiagnosticsData] = useState([]);
  const [startDateFilter, setStartDateFilter] = useState(null);
  const [endDateFilter, setEndDateFilter] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showDeviceDiagnosticsDataList, setShowDeviceDiagnosticsDataList] =
    useState(false);
  const [isDataDownloading, setIsDataDownloading] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(deviceDiagnosticsPageSize);
  const today = new Date();
  const getDefaultDateRange = () => {
    const now = new Date();
    const startOfDay = new Date(now);
    startOfDay.setHours(0, 0, 0, 0);
    return { startOfDay, now };
  };

  const handleDiagnosticsPageChange = (newPage) => {
    if (newPage === pageIndex) return;
    setPageIndex(newPage);
    fetchDeviceDiagnosticsData(newPage);
  };

  const handleDiagnosticsPageSizeChange = (newSize) => {
    if (!newSize || newSize === pageSize) return;
    setPageSize(newSize);
    setPageIndex(0);
    fetchDeviceDiagnosticsData(
      0,
      getFromTimestamp(startDateFilter),
      getToTimestamp(endDateFilter),
      newSize
    );
  };

  const getFromTimestamp = (dateFilter) => {
    if (!dateFilter) return undefined;
    const start = new Date(dateFilter);
    start.setHours(0, 0, 0, 0);
    return start.getTime();
  };

  const getToTimestamp = (dateFilter) => {
    if (!dateFilter) return undefined;
    const end = new Date(dateFilter);
    end.setHours(23, 59, 59, 999);
    return Math.min(end.getTime(), Date.now());
  };

  const fetchDeviceDiagnosticsData = (
    page = pageIndex,
    fromTs = getFromTimestamp(startDateFilter),
    toTs = getToTimestamp(endDateFilter),
    size = pageSize
  ) => {
    setIsLoading(true);
    getDeviceDiagnosticsData(
      {
        deviceId: deviceDetails?.deviceId ?? "",
        fromTs,
        toTs,
        index: page,
        size,
      },
      () => {
        setIsLoading(false);
        setShowDeviceDiagnosticsDataList(true);
      }
    );
  };

  const reset = () => {
    const { startOfDay, now } = getDefaultDateRange();
    setStartDateFilter(startOfDay);
    setEndDateFilter(now);
    setPageIndex(0);
    fetchDeviceDiagnosticsData(
      0,
      getFromTimestamp(startOfDay),
      getToTimestamp(now)
    );
  };

  const filter = () => {
    if (startDateFilter && endDateFilter && startDateFilter > endDateFilter) {
      return;
    }
    setPageIndex(0);
    fetchDeviceDiagnosticsData(0);
  };

  const handleStartDateChange = (date) => {
    setStartDateFilter(date);
    setEndDateFilter(null);
    setShowDeviceDiagnosticsDataList(false);
  };

  const handleEndDateChange = (date) => {
    setEndDateFilter(date);
    setShowDeviceDiagnosticsDataList(false);
  };

  function handleDownloadDiagnosticsDeviceData() {
    if (startDateFilter && endDateFilter && startDateFilter > endDateFilter) {
      return;
    }
    setIsDataDownloading(true);
    downloadDeviceDiagnosticReport(
      {
        fromTs: getFromTimestamp(startDateFilter),
        toTs: getToTimestamp(endDateFilter),
        deviceId: deviceDetails.deviceId,
        printableStartDate: `${format(startDateFilter, "dd MMM yyyy")}-${format(endDateFilter, "dd MMM yyyy")}`,
      },
      () => {
        setIsDataDownloading(false);
      }
    );
  }

  const minStartDate = (() => {
    const futureDate = new Date();
    return new Date(
      futureDate?.setDate(futureDate?.getDate() - startDateLimit)
    );
  })();

  useEffect(() => {
    const { startOfDay, now } = getDefaultDateRange();
    setStartDateFilter(startOfDay);
    setEndDateFilter(now);
    setIsLoading(true);
    getDeviceDiagnosticsData(
      {
        deviceId: deviceDetails?.deviceId,
        fromTs: getFromTimestamp(startOfDay),
        toTs: getToTimestamp(now),
        index: 0,
        size: pageSize,
      },
      () => {
        setIsLoading(false);
        setShowDeviceDiagnosticsDataList(true);
      }
    );
    setPageIndex(0);
  }, [deviceDetails?.deviceId]);

  useEffect(() => {
    setDeviceDiagnosticsData(diagnosticData);
  }, [diagnosticData]);

  return (
    <Fragment>
      <Box sx={{ width: "100%", minWidth: 0 }}>
        <Paper sx={{ p: 2, boxShadow: 2, borderRadius: 1, overflow: "hidden" }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                lg: "minmax(0, 220px) minmax(0, 220px) auto",
              },
              gap: 2,
              alignItems: "center",
            }}
          >
            <Box sx={{ minWidth: 0, width: "100%" }}>
              <MuiDatePicker
                label="Start Date"
                value={startDateFilter}
                onChange={handleStartDateChange}
                minDate={minStartDate}
                maxDate={endDateFilter || today}
                sx={{ width: "100%" }}
              />
            </Box>
            <Box sx={{ minWidth: 0, width: "100%" }}>
              <MuiDatePicker
                label="End Date"
                value={endDateFilter}
                onChange={handleEndDateChange}
                minDate={startDateFilter}
                maxDate={today}
                sx={{ width: "100%" }}
              />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                  alignItems: "center",
                }}
              >
                <AppButton
                  variant="primary"
                  size="medium"
                  onClick={filter}
                  disabled={startDateFilter === null || endDateFilter === null || startDateFilter > endDateFilter}
                  sx={{ height: 40 }}
                >
                  Filter
                </AppButton>
                <AppButton variant="ghost" size="medium" onClick={reset} sx={{ height: 40 }}>
                  Reset
                </AppButton>
                <AppButton
                  variant="primary"
                  size="medium"
                  onClick={handleDownloadDiagnosticsDeviceData}
                  disabled={
                    isDataDownloading ||
                    startDateFilter === null ||
                    endDateFilter === null ||
                    startDateFilter > endDateFilter
                  }
                  sx={{ height: 40 }}
                >
                  {isDataDownloading ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CircularProgress size={20} sx={{ color: "inherit" }} />
                      Download
                    </Box>
                  ) : (
                    "Download"
                  )}
                </AppButton>
              </Box>
            </Box>
          </Box>
        </Paper>

      {isLoading && (
        <Box
          sx={{
            display: "grid",
            placeItems: "center",
            height: "20vh",
            mt: 2,
          }}
        >
          <CircularProgress size={60} sx={{ color: "#AFB42B" }} />
        </Box>
      )}

      {!isLoading && showDeviceDiagnosticsDataList && (
        <Paper
          sx={{
            p: 2,
            boxShadow: 2,
            borderRadius: 1,
            mt: 2,
            mb: 2,
            overflow: "hidden",
          }}
        >
          {!Array.isArray(deviceDiagnosticsData) || deviceDiagnosticsData.length === 0 ? (
            <EmptyState
              message="No Data found"
              sx={{ bgcolor: "transparent", p: 0 }}
            />
          ) : (
            <DisplayDeviceDiagnosticsData
              setDialog={setDialog}
              setDialogContents={setDialogContents}
              deviceCategory={deviceDetails.category}
              deviceDiagnosticsData={deviceDiagnosticsData}
            />
          )}
          {Array.isArray(deviceDiagnosticsData) && deviceDiagnosticsData.length > 0 && (
            <IncrementalPagination
              page={pageIndex}
              pageSize={pageSize}
              dataLength={deviceDiagnosticsData.length}
              onPageChange={handleDiagnosticsPageChange}
              onPageSizeChange={handleDiagnosticsPageSizeChange}
              pageSizeOptions={deviceDiagnosticsPageSizeOptions}
              isLastPage={
                deviceDiagnosticsData.length < pageSize
              }
              sx={{ m: 0, mt: 1.5, px: 0 }}
            />
          )}
        </Paper>
      )}
      </Box>
    </Fragment>
  );
};

export default DeviceDiagnostics;
